import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Eye, CheckCircle, Clock, Loader, X, FileText, Download, ZoomIn, ZoomOut, Printer } from "lucide-react";
import { api } from "../api";

export default function History() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [zoom, setZoom] = useState(false);
  const navigate = useNavigate();

  const handleDownloadPreview = (bill) => {
    if (!bill || !bill.preview_image_base64) return;
    const link = document.createElement("a");
    link.href = bill.preview_image_base64;
    link.download = `statement_${bill.bill_date}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintPreview = (bill) => {
    if (!bill || !bill.preview_image_base64) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Statement Preview - ${bill.bill_date}</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #0b0f19; }
            img { max-width: 100%; max-height: 100vh; object-fit: contain; }
          </style>
        </head>
        <body>
          <img src="${bill.preview_image_base64}" onload="window.print(); window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const loadBills = useCallback(async () => {
    try {
      const data = await api.getBills();
      setBills(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBills();
  }, [loadBills]);

  const handleDelete = async (bill) => {
    const label = `bill dated ${bill.bill_date}`;
    if (!window.confirm(`Delete the ${label}? The PDF in Google Drive will NOT be deleted.`)) return;
    setDeleting(bill.id);
    try {
      await api.deleteBill(bill.id);
      setBills((prev) => prev.filter((b) => b.id !== bill.id));
    } catch (e) {
      alert("Delete failed: " + e.message);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader size={32} className="animate-pulse-slow text-text-muted" />
      </div>
    );
  }

  return (
    <>
      {/* Fullscreen Interactive Document Review Center */}
      {selectedBill && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-8 animate-fade-in"
          style={{ background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(12px)" }}
          onClick={() => setSelectedBill(null)}
        >
          <div
            className="relative w-full max-w-5xl h-[90vh] md:h-[80vh] bg-surface border border-border shadow-lg rounded-3xl overflow-hidden animate-zoom-in flex flex-col md:grid md:grid-cols-[1fr_360px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button (top right of modal container for quick mobile action) */}
            <button
              onClick={() => setSelectedBill(null)}
              className="absolute top-4 right-4 md:hidden p-1.5 rounded-xl text-text-muted hover:text-text-primary bg-surface border border-border transition-all active:scale-95 z-[100]"
            >
              <X size={15} />
            </button>

            {/* Left Column: Scrollable Document Viewport */}
            <div className="relative flex-1 flex items-center justify-center p-4 md:p-6 overflow-y-auto h-[55vh] md:h-full bg-slate-50 dark:bg-slate-950/30 select-none border-b md:border-b-0 border-border">
              
              {/* Document Image with zoom modifier */}
              <div 
                className="relative transition-all duration-300 max-w-full max-h-full"
                style={{ display: "contents" }}
              >
                <img
                  src={selectedBill.preview_image_base64}
                  alt="Statement Preview"
                  onClick={() => setZoom(!zoom)}
                  className={`transition-all duration-300 rounded-xl border border-border shadow-lg select-none ${
                    zoom 
                      ? "max-w-[140%] max-h-none h-auto w-auto scale-110 cursor-zoom-out scroll-m-4" 
                      : "max-w-full max-h-[50vh] md:max-h-[72vh] h-auto w-auto object-contain cursor-zoom-in hover:shadow-xl hover:border-border"
                  }`}
                />
              </div>

              {/* Floating Zoom Indicator Action Badge */}
              <button
                onClick={() => setZoom(!zoom)}
                title={zoom ? "Fit to screen" : "Zoom in"}
                className="absolute bottom-4 left-4 p-2 rounded-xl text-text-secondary hover:text-text-primary bg-surface border border-border shadow-md transition-all active:scale-95 flex items-center gap-1.5 text-[11px] font-bold"
              >
                {zoom ? (
                  <>
                    <ZoomOut size={13} />
                    <span>Fit View</span>
                  </>
                ) : (
                  <>
                    <ZoomIn size={13} />
                    <span>Zoom In</span>
                  </>
                )}
              </button>
            </div>

            {/* Right Column: Statement Information & Control Sidebar */}
            <div className="border-t md:border-t-0 md:border-l border-border bg-surface-2 p-5 md:p-6 flex flex-col justify-between h-[35vh] md:h-full overflow-y-auto animate-fade-in">
              
              {/* Upper Details Group */}
              <div className="space-y-4">
                
                {/* Document Metadata header (Filename) */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-accent-light text-accent border border-accent/15 shrink-0">
                    <FileText size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-extrabold text-text-secondary tracking-wider leading-none mb-1.5">Monitored File</div>
                    <div className="text-[12px] font-mono font-bold text-text-primary truncate" title={selectedBill.drive_file_name}>
                      {selectedBill.drive_file_name || "Calculated_Statement.pdf"}
                    </div>
                  </div>
                </div>

                {/* Status and Title */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <h2 className="font-display text-[18px] md:text-[20px] font-extrabold text-text-primary tracking-tight">
                      Statement Review
                    </h2>
                    {selectedBill.finalized ? (
                      <span className="inline-flex items-center gap-1 bg-success-light text-success rounded-full px-2.5 py-0.5 text-[10px] font-bold border border-success/10 shadow-[inset_0_1px_1px_rgba(76,175,110,0.05)]">
                        <CheckCircle size={10} />
                        Finalized
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-accent-light text-accent rounded-full px-2.5 py-0.5 text-[10px] font-bold border border-accent/10 shadow-[inset_0_1px_1px_rgba(249,115,22,0.05)]">
                        <Clock size={10} />
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="text-[12.5px] text-text-secondary leading-relaxed font-medium">
                    Extracted billing record details for the Calgary property.
                  </p>
                </div>

                <hr className="border-border my-1" />

                {/* Date Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-surface border border-border rounded-2xl p-2.5">
                    <div className="text-[9.5px] uppercase font-extrabold text-text-secondary tracking-wider mb-1">Bill Date</div>
                    <div className="font-mono text-[12px] text-text-primary font-bold">{selectedBill.bill_date}</div>
                  </div>
                  <div className="bg-surface border border-border rounded-2xl p-2.5">
                    <div className="text-[9.5px] uppercase font-extrabold text-text-secondary tracking-wider mb-1">Due Date</div>
                    <div className="font-mono text-[12px] text-text-primary font-bold">{selectedBill.due_date}</div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="bg-surface border border-border rounded-2xl p-3.5 space-y-2.5 shadow-sm">
                  <div className="text-[10px] uppercase font-extrabold text-text-secondary tracking-wider leading-none mb-1.5">
                    Cost Distribution
                  </div>

                  {/* Grand total */}
                  <div className="flex justify-between items-center text-[12px]">
                    <span className="text-text-secondary font-medium">Grand Total Due</span>
                    <span className="font-mono font-extrabold text-text-primary">
                      ${selectedBill.totals?.grand_total?.toFixed(2) || "0.00"}
                    </span>
                  </div>

                  {/* Tenant total */}
                  <div className="flex justify-between items-center pt-2 border-t border-border text-[12px]">
                    <span className="text-text-secondary font-semibold flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent"></span>
                      Tenant Share
                    </span>
                    <span className="font-mono font-extrabold text-accent">
                      ${selectedBill.totals?.tenant_total?.toFixed(2) || "0.00"}
                    </span>
                  </div>

                  {/* Landlord total */}
                  <div className="flex justify-between items-center pt-2 border-t border-border text-[12px]">
                    <span className="text-text-secondary font-medium flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-text-secondary/50"></span>
                      Landlord Share
                    </span>
                    <span className="font-mono font-bold text-text-secondary">
                      ${selectedBill.totals?.landlord_total?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                </div>

              </div>

              {/* Bottom Interactive Action Controls */}
              <div className="space-y-2 mt-4">
                <button
                  onClick={() => {
                    setSelectedBill(null);
                    navigate(`/?bill=${selectedBill.id}`);
                  }}
                  className="w-full py-2.5 rounded-xl text-[12.5px] font-bold text-white bg-accent hover:bg-accent-hover shadow-lg shadow-accent/15 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                >
                  <Eye size={14} />
                  Open in Splitter
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownloadPreview(selectedBill)}
                    title="Save first page JPEG to local computer"
                    className="flex-1 py-2 rounded-xl text-[11.5px] font-semibold text-text-secondary bg-surface border border-border hover:bg-surface-2 hover:text-text-primary hover:border-text-muted transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
                  >
                    <Download size={13} />
                    Download
                  </button>
                  <button
                    onClick={() => handlePrintPreview(selectedBill)}
                    title="Print the utility bill preview sheet"
                    className="py-2 px-3 rounded-xl text-text-secondary bg-surface border border-border hover:bg-surface-2 hover:text-text-primary hover:border-text-muted transition-all active:scale-[0.98] flex items-center justify-center"
                  >
                    <Printer size={13} />
                  </button>
                  <button
                    onClick={() => setSelectedBill(null)}
                    className="flex-1 py-2 rounded-xl text-[11.5px] font-semibold text-text-secondary bg-surface border border-border hover:bg-surface-2 hover:text-text-primary hover:border-text-muted transition-all active:scale-[0.98]"
                  >
                    Close
                  </button>
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

      <div className="animate-fade-in relative">
        <div className="mb-7">
          <h1 className="font-display text-[30px] font-extrabold mb-1 text-text-primary tracking-tight">
            Bill History
          </h1>
          <div className="text-[14px] text-text-muted">
            {bills.length} bill{bills.length !== 1 ? "s" : ""} on record
          </div>
        </div>

      {bills.length === 0 ? (
        <div className="text-center py-16 px-6 text-text-muted text-[15px]">
          No bills recorded yet.
        </div>
      ) : (
        <div className="glass-panel overflow-hidden border-border">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_1fr_140px_120px_90px_100px] px-5 py-2.5 bg-slate-100/80 dark:bg-slate-900/60 border-b border-border text-[10px] text-text-secondary uppercase tracking-[0.08em] font-extrabold">
            <span>Bill Date</span>
            <span>Due Date</span>
            <span className="text-right">Tenant Total</span>
            <span className="text-center">Status</span>
            <span className="text-center">Preview</span>
            <span className="text-right">Actions</span>
          </div>

          {bills.map((bill, i) => (
            <div
              key={bill.id}
              className={`grid grid-cols-[1fr_1fr_140px_120px_90px_100px] px-5 py-4 items-center transition-colors duration-150 hover:bg-white/[0.02] ${
                i < bills.length - 1 ? "border-b border-border" : ""
              }`}
            >
              <span className="font-mono text-[13px] text-text-primary">
                {bill.bill_date}
              </span>
              <span className="font-mono text-[13px] text-text-secondary">
                {bill.due_date}
              </span>
              <span className="font-mono text-[15px] font-bold text-right text-accent">
                ${bill.totals?.tenant_total?.toFixed(2)}
              </span>
              <div className="flex justify-center">
                {bill.finalized ? (
                  <span className="inline-flex items-center gap-1.5 bg-success-light text-success rounded-full px-2.5 py-0.5 text-[12px] font-semibold border border-success/20 shadow-[inset_0_1px_1px_rgba(76,175,110,0.1)]">
                    <CheckCircle size={11} />
                    Finalized
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 bg-accent-light text-accent rounded-full px-2.5 py-0.5 text-[12px] font-semibold border border-accent/20 shadow-[inset_0_1px_1px_rgba(232,133,58,0.1)]">
                    <Clock size={11} />
                    Draft
                  </span>
                )}
              </div>
              <div className="flex justify-center">
                {bill.preview_image_base64 ? (
                  <div className="relative group cursor-pointer" onClick={() => { setSelectedBill(bill); setZoom(false); }}>
                    <img
                      src={bill.preview_image_base64}
                      alt="Statement Preview"
                      className="w-8 h-10 object-cover rounded-md border border-border shadow-sm transition-all duration-200 group-hover:scale-105 group-hover:shadow-md"
                    />
                    <div className="absolute inset-0 bg-black/10 rounded-md opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                      <Eye size={10} className="text-white" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-[11px] text-text-muted select-none" title="No preview available for older bills">
                    <FileText size={12} className="opacity-40" />
                    <span className="opacity-40 font-medium">—</span>
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 justify-end">
                <button
                  onClick={() => navigate(`/?bill=${bill.id}`)}
                  title="View bill"
                  className="p-1.5 border border-white/10 rounded-lg text-text-secondary hover:bg-surface-2 hover:text-text-primary hover:border-white/20 transition-all active:scale-95"
                >
                  <Eye size={14} />
                </button>
                <button
                  onClick={() => handleDelete(bill)}
                  disabled={deleting === bill.id}
                  title="Delete record"
                  className={`p-1.5 border rounded-lg transition-all active:scale-95 flex items-center ${
                    deleting === bill.id
                      ? "border-border text-text-muted bg-transparent"
                      : "border-danger/30 text-danger hover:bg-danger-light hover:border-danger/50"
                  }`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
